/**
 * LoadingReferences — showcases our real projects while AI processes the photo.
 *
 * UX: converts the 20–40 s dead-time into a mini portfolio carousel.
 * Auto-rotates every 4.5 s with fade+slide. Randomises on mount so different
 * visitors don't always see the same first slide.
 *
 * Data: fetches `/api/projects` (public), keeps only visible ones with a
 * cover image. If the fetch fails or returns nothing, the component quietly
 * renders nothing so the loader is unaffected.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Calendar, Star, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ROTATE_MS = 4500;

const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const fullUrl = (u) => (u && u.startsWith("/") ? `${backendOrigin}${u}` : u);

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function LoadingReferences() {
  const [projects, setProjects] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // React 18 StrictMode mounts effects twice in dev; the `alive` guard
    // pattern breaks here because the first invocation's cleanup fires before
    // the axios response resolves, and the .then would early-return. In React
    // 18 setState after unmount is a silent no-op, so we drop the guard.
    axios
      .get(`${API}/projects`)
      .then((r) => {
        const rows = (r.data || [])
          .filter((p) => p.visible !== false)
          .filter((p) => p.image || (p.gallery && p.gallery.length > 0));
        // Prefer featured but always mix in a few standard ones.
        const featured = rows.filter((p) => p.featured);
        const other = rows.filter((p) => !p.featured);
        const combined = [...shuffle(featured), ...shuffle(other)].slice(0, 8);
        setProjects(combined);
      })
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    if (projects.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % projects.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [projects.length]);

  const current = useMemo(() => projects[idx], [projects, idx]);

  if (!current) return null;

  return (
    <div className="mt-8" data-testid="estimator-references">
      <div className="text-center mb-3">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
          <Star size={10} className="text-[#FF5E00]" />
          Kým čakáte, pozrite si naše realizácie
        </div>
      </div>

      <div className="relative bg-black/60 border border-white/10 rounded-2xl overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            data-testid={`estimator-ref-${current.id}`}
          >
            <div className="relative aspect-[16/10] bg-zinc-900">
              <img
                src={fullUrl(current.image || current.gallery?.[0]?.url)}
                alt={current.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Dim gradient for legible text on any image */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/70 to-transparent" />
              {current.featured && (
                <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-[#FF5E00] text-black text-[9px] font-black uppercase tracking-widest">
                  Referencia roka
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 p-4">
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-bold mb-1">
                  {current.category}
                </div>
                <div className="text-base font-black leading-tight text-white drop-shadow">
                  {current.title}
                </div>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <div className="flex items-center gap-3 text-[11px] text-zinc-300 min-w-0">
                    {current.location && (
                      <span className="inline-flex items-center gap-1 truncate">
                        <MapPin size={10} className="text-[#00E5FF]" />
                        {current.location}
                      </span>
                    )}
                    {current.year && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={10} className="text-[#00E5FF]" />
                        {current.year}
                      </span>
                    )}
                  </div>
                  <a
                    href={`/projekty#${current.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#00E5FF] hover:brightness-110 text-black text-[10px] font-black uppercase tracking-wider transition-all"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`estimator-ref-open-${current.id}`}
                    title="Otvoriť projekt v novej karte"
                  >
                    Pozrieť projekt
                    <ExternalLink size={10} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress dots */}
      {projects.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {projects.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setIdx(i)}
              aria-label={`Ukázať referenciu ${i + 1}`}
              className={`h-1 rounded-full transition-all ${
                i === idx ? "w-6 bg-[#00E5FF]" : "w-1.5 bg-white/20 hover:bg-white/40"
              }`}
              data-testid={`estimator-ref-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
